import { useCallback, useEffect } from 'react';
import { IntlProvider } from 'react-intl';

import { GlobalTextStyle } from '@mindtickle/styles/lib/globalStyles';

import type { TApplicationProps } from '~/components/Application';
import Application from '~/components/Application';
import Auth from '~/components/Auth/Provider';
import BaseDomainProvider from '~/components/BaseDomain';
/* Intentionally importing with absolute path so that other exports don't get executed upfront */
import BlockingIntegrations from '~/components/Integrations/components/BlockingIntegrations';
import { ErrorBoundary } from '~/components/Tools';
import {
  DEFAULT_ERROR_FALLBACK as ErrorFallback,
  PERFORMANCE_MEASURE_NAMES,
  UXR_COOKIE_NAME,
  UXR_COOKIE_EXPIRY_DAYS,
} from '~/config/app.constants';
import ENV from '~/config/env';
import { hideSplashScreen, setCookie } from '~/utils';
import type { Overwrite } from '~/utils/types';

import { sendCustomTimingToDatadog } from './components/Datadog/helpers';
import loadfonts from './components/Fonts';
import { prefetchRequiredApps } from './utils/prefetchRequiredApps';

loadfonts();

type TRootProps = Overwrite<TApplicationProps, { isProtected?: boolean; isPublic?: boolean }>;

// eslint-disable-next-line complexity
export default (props: TRootProps & { graphqlEndpoints?: string[] }) => {
  const isDatadogEnabled = props.enableDatadog && ENV.DATADOG_ENABLED;
  const isProtected = !!props.isProtected || !props.isPublic;

  // [Todo] Need to dreprecate graphqlEndpoints prop
  const graphqlEndpoint = props.graphqlEndpoint || props.graphqlEndpoints?.[0];

  useEffect(() => {
    try {
      // this pre-fetching is happening while auth call is in progress
      prefetchRequiredApps();
      sendCustomTimingToDatadog(PERFORMANCE_MEASURE_NAMES.SHELL_RENDERING_COMPLETED);
      // Ensure UXR cookie is set for the current host on root path
      // setCookie from @mindtickle/utils takes expiry in days (number)
      setCookie(UXR_COOKIE_NAME, process.env.UI_SHELL_TRACK, UXR_COOKIE_EXPIRY_DAYS);
    } catch (e) {
      console.error(e); // eslint-disable-line
    }
    //Hide splash screen when react is loaded
    hideSplashScreen();
  }, []);
  const onAuthLoaded = useCallback(() => {
    sendCustomTimingToDatadog(PERFORMANCE_MEASURE_NAMES.AUTH_RENDERING_COMPLETED);
  }, []);

  return (
    // TODO: Update intlprovider with hook/hoc
    // As ErrorBoundary is used outside, we need to wrap it with IntlProvider because Context is needed for intl messages are used in Error Component
    // Need to handle this with differnet approach as auth object is not available at initial render
    // So hardcoding locale here for stencil 2 activity to unblock the develpment and sanity
    <IntlProvider locale="en" messages={{}}>
      <ErrorBoundary fallback={ErrorFallback}>
        <BlockingIntegrations>
          <BaseDomainProvider>
            <Auth onAuthLoaded={onAuthLoaded}>
              <ErrorBoundary fallback={ErrorFallback}>
                <GlobalTextStyle />
                <Application
                  {...props}
                  isProtected={isProtected}
                  graphqlEndpoint={graphqlEndpoint}
                  enableDatadog={isDatadogEnabled}
                />
              </ErrorBoundary>
            </Auth>
          </BaseDomainProvider>
        </BlockingIntegrations>
      </ErrorBoundary>
    </IntlProvider>
  );
};
